import React, { FC, useEffect, useState } from 'react';
import * as actions from '../mfaTokensActions';
import { Field } from '../types';
import { MICROSOFT_MFA_TOKEN_FIELDS } from '../../../common/fields';
import TokenTextField from './TokenTextField';
import { GENERATE_CERTIFICATE_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import TokenActions from './TokenActions';
import useGenerateCertificate from '../api/mutations/useGenerateCertificate';
import { TokenComponentProps } from './index';
import { showAlert } from '@silverfort/sf-components';
import UserIdentifierDropdown from './UserIdentifierDropdown';

type DispatchFn = (action: UpdateAction | AddTenantAction | UpdateTenantAction) => void;

interface PublicKey {
    publicKey: string;
    tenantId: string;
    name: string;
    lastModified: number;
}

interface UpdateAction {
    type: typeof actions.UPDATE_MICROSOFT_MFA_TOKEN;
    payload: {
        fieldId: string;
        value: string;
    };
}

interface AddTenantAction {
    type: typeof actions.ADD_MICROSOFT_TENANT;
    payload: PublicKey;
}

interface UpdateTenantAction {
    type: typeof actions.UPDATE_MICROSOFT_TENANT_NAME;
    payload: Pick<PublicKey, 'tenantId' | 'name'>;
}

export interface MicrosoftFields {
    aadUpnField: Field;
    aadPublicKeys: Field;
}

type MicrosoftProps = TokenComponentProps<MicrosoftFields, DispatchFn>;

const Microsoft: FC<MicrosoftProps> = ({ fields, dispatch, onSave, onCancel, editTenantId }) => {
    const aadUpnField = fields[MICROSOFT_MFA_TOKEN_FIELDS.AAD_UPN_FIELD.ID];
    const [microsoftTenant, setMicrosoftTenant] = useState({
        newTenantId: '',
        newTenantName: ''
    });

    const isSomeFieldsEmpty = Object.keys(microsoftTenant).some((key) => microsoftTenant[key] === '');

    const onGenerateSuccess = (data: string) => {
        dispatchAddTenant({
            publicKey: data,
            tenantId: microsoftTenant.newTenantId,
            name: microsoftTenant.newTenantName,
            lastModified: Date.now()
        });
        onSave();
    };

    const onGenerateError = () => {
        showAlert('Failed to generate certificate', 'error');
    };

    const { generateCertificateMutation, isLoading } = useGenerateCertificate({
        onSuccessCallback: onGenerateSuccess,
        onErrorCallback: onGenerateError
    });

    const dispatchUpdate = (fieldId: string, value: string) => {
        return dispatch({
            type: actions.UPDATE_MICROSOFT_MFA_TOKEN,
            payload: { fieldId, value }
        });
    };

    const dispatchAddTenant = (value: PublicKey) => {
        return dispatch({
            type: actions.ADD_MICROSOFT_TENANT,
            payload: value
        });
    };

    const dispatchUpdateTenantName = (tenantId: string, name: string) => {
        return dispatch({
            type: actions.UPDATE_MICROSOFT_TENANT_NAME,
            payload: { tenantId, name }
        });
    };

    const handleSave = () => {
        if (editTenantId) {
            dispatchUpdateTenantName(editTenantId, microsoftTenant.newTenantName);
            onSave();
            return;
        }

        generateCertificateMutation({
            certificateType: GENERATE_CERTIFICATE_TYPES.AAD,
            fields: { tenantID: microsoftTenant.newTenantId }
        });
    };

    useEffect(() => {
        if (editTenantId) {
            const tenant = fields[MICROSOFT_MFA_TOKEN_FIELDS.AAD_PUBLIC_KEY.ID].value.tenants.find(
                (tenant: PublicKey) => tenant.tenantId === editTenantId
            );
            if (tenant) {
                setMicrosoftTenant({ newTenantId: tenant.tenantId, newTenantName: tenant.name });
            }
        }
    }, [editTenantId, fields]);

    const aadUpnOptions = (aadUpnField.options || []).map((option) => ({
        title: option.label,
        value: option.value
    }));

    return (
        <>
            <UserIdentifierDropdown
                options={aadUpnOptions}
                selectedOption={aadUpnField.value}
                onChange={(val) => dispatchUpdate(aadUpnField.id, val)}
                mandatory
                label={aadUpnField.name}
            />

            <TokenTextField
                label="Tenant name"
                value={microsoftTenant.newTenantName}
                onChange={(val) => setMicrosoftTenant({ ...microsoftTenant, newTenantName: val })}
                isSecret={false}
            />
            <TokenTextField
                label="Tenant ID"
                value={microsoftTenant.newTenantId}
                onChange={(val) => setMicrosoftTenant({ ...microsoftTenant, newTenantId: val })}
                isSecret={false}
                disabled={editTenantId !== undefined}
            />

            <TokenActions
                disabled={isLoading}
                onSave={handleSave}
                onCancel={onCancel}
                saveDisabled={isSomeFieldsEmpty}
            />
        </>
    );
};

export default Microsoft;
